import { useState, useCallback, useEffect, useRef } from 'react';
import { useWhiteboardStore } from '../store/useWhiteboardStore';
import { useAuthStore } from '../store/useAuthStore';
import { useTenantStore } from '../store/useTenantStore';
import { useTestStore } from '../store/useTestStore';
import { AnswerDataService } from '../lib/firebase/answerDataService';
import { BulkSyncService } from '../lib/firebase/bulkSyncService';
import { AnswerData } from '../types/whiteboardProps';
import { QuestionData } from '../types/questionData';
import { OpticalAnswer } from '../types';

interface UseAnswerDataProps {
  testId: string;
  studyTaskId: string;
  testTitle: string;
  totalQuestions: number;
  bulkSubmitUrl?: string;
  isExternalMode?: boolean;
  userId?: string | number;
  tenantId?: string | number;
  onSyncStatusChange?: (status: {
    isSyncing: boolean;
    syncedToMssql: boolean;
    syncAttempts: number;
    lastSyncError?: string;
  }) => void;
  onBulkSubmitSuccess?: (bulkData: any) => void;
  onBulkSubmitError?: (error: Error) => void;
}

export const useAnswerData = ({
  testId,
  studyTaskId,
  testTitle,
  totalQuestions,
  bulkSubmitUrl,
  isExternalMode = false,
  userId: providedUserId,
  tenantId: providedTenantId,
  onSyncStatusChange,
  onBulkSubmitSuccess,
  onBulkSubmitError
}: UseAnswerDataProps) => {
  const storeUserId = useAuthStore((state) => state.userId);
  const storeTenantId = useTenantStore((state) => state.tenantId);
  
  // Check if we're in WebView mode
  const isWebViewMode = window.parent !== window || !!(window as any).ReactNativeWebView;
  
  // Use provided values or fall back to store values
  const userId = providedUserId || storeUserId;
  // In WebView mode, always use tenantId 50000
  const tenantId = isWebViewMode ? 50000 : (providedTenantId || storeTenantId);
  const { 
    opticalAnswers, 
    strokes,
    getCurrentQuestionId,
    questionDataCache
  } = useWhiteboardStore();
  
  
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<{
    syncedToMssql: boolean;
    syncAttempts: number;
  }>({ syncedToMssql: false, syncAttempts: 0 });
  
  const lastSavedAnswersRef = useRef<Record<string, OpticalAnswer>>({});
  const saveTimeoutRef = useRef<NodeJS.Timeout>();
  const opticalAnswersRef = useRef(opticalAnswers);
  const questionDataCacheRef = useRef(questionDataCache);
  
  // Keep refs updated
  useEffect(() => {
    opticalAnswersRef.current = opticalAnswers;
  }, [opticalAnswers]);
  
  useEffect(() => {
    questionDataCacheRef.current = questionDataCache;
  }, [questionDataCache]);
  
  // Initialize test metadata on mount
  useEffect(() => {
    const initializeTest = async () => {
      if (userId && tenantId && studyTaskId && !isInitialized) {
        // In external mode, check if we have Firebase configured
        if (isExternalMode) {
          const hasFirebase = useWhiteboardStore.getState().storageService !== null;
          if (!hasFirebase) {
            console.log('⚠️ External mode without Firebase - skipping test metadata creation');
            return;
          }
        }
        
        // REMOVED: Test metadata creation - WhiteboardProvider handles this
        // We only need to mark as initialized for answer saving functionality
        setIsInitialized(true);

        // If bulk submit URL is provided, store it for later use
        if (bulkSubmitUrl) {
          console.log('📦 Bulk submit URL configured for this test');
        }
      }
    };
    
    // Add a small delay to ensure Firebase auth has time to complete
    const timeoutId = setTimeout(() => {
      initializeTest();
    }, 1000); // 1 second delay
    
    return () => clearTimeout(timeoutId);
  }, [testId, studyTaskId, userId, tenantId, testTitle, totalQuestions, bulkSubmitUrl, isExternalMode, isInitialized]);
  
  // REMOVED: updateTestMetadata effect - WhiteboardProvider handles metadata updates
  
  // Save answer data when optical answer changes
  const saveAnswer = useCallback(async (questionId: string, answer: OpticalAnswer) => {
    if (!userId || !tenantId || !studyTaskId) {
      console.log('❌ Cannot save answer - missing required data:', { userId, tenantId, studyTaskId });
      return;
    }
    
    // Validate that questionId belongs to current test
    const currentQuestions = useTestStore.getState().questions;
    const isValidQuestion = currentQuestions.some(q => q.questionId === questionId);
    
    if (!isValidQuestion) {
      console.warn(`⚠️ Question ${questionId} does not belong to current test ${testId} - skipping save`);
      return;
    }
    
    // In external mode, check if we have Firebase configured
    if (isExternalMode) {
      const hasFirebase = useWhiteboardStore.getState().storageService !== null;
      if (!hasFirebase) {
        console.log('⚠️ External mode without Firebase - skipping single answer save');
        return;
      }
    }
    
    
    try {
      // Get the most current cache from the store
      const mostCurrentCache = useWhiteboardStore.getState().questionDataCache;
      const currentQuestionData = mostCurrentCache.get(questionId);
      
      // Get selection history from question data cache
      const selectionHistory = currentQuestionData?.selectionHistory || '';
      const trajectoryForAnswerHistory = selectionHistory || null;
      
     
      
      // Get eliminated choices from store and convert to string format
      const eliminatedChoicesStore = useTestStore.getState().eliminatedChoices;
      const eliminatedForQuestion = eliminatedChoicesStore[questionId] || '';
      const eliminationTrajectory = eliminatedForQuestion || currentQuestionData?.eliminatedChoices || null;
      
      // Also save eliminated choices to cache for persistence
      if (eliminatedForQuestion && currentQuestionData) {
        currentQuestionData.eliminatedChoices = eliminatedForQuestion;
      }
      
      // Get or create time trajectory data - make a copy to avoid mutations
      let timeTrajectory = currentQuestionData?.trajectory ? [...currentQuestionData.trajectory] : [];
      
      // Only log trajectory details in debug mode or if there's an issue
      if (timeTrajectory.length === 0 || process.env.NODE_ENV === 'development') {
        console.log('🕒 Trajectory status:', {
          questionId: questionId.substring(0, 8) + '...',
          sessionCount: timeTrajectory.length,
          hasActiveSession: timeTrajectory.some(t => !t.endDate)
        });
      }
      
      // Format intervals from trajectory data - include all sessions (completed and active)
      let intervals: string | null = null;
      if (timeTrajectory.length > 0) {
        // Process all sessions, including active ones
        const formattedIntervals = timeTrajectory.map((session, index) => {
          let duration = session.duration;
          let endTime = session.endDate;
          
          // For active sessions (no endDate), calculate current duration
          if (!session.endDate && !session.duration) {
            const currentTime = Date.now();
            duration = (currentTime - session.startDate) / 1000;
            endTime = currentTime;
            // Active session - silently calculate current duration
          } else if (!duration && session.endDate) {
            // Calculate duration if not stored
            duration = (session.endDate - session.startDate) / 1000;
          }
          
          return {
            start: new Date(session.startDate).toISOString().replace('T', ' ').substring(0, 19),
            end: endTime ? new Date(endTime).toISOString().replace('T', ' ').substring(0, 19) : null,
            duration: Math.round((duration || 0) * 100) / 100
          };
        });
        
        intervals = JSON.stringify(formattedIntervals);
        // Only log in development mode
        if (process.env.NODE_ENV === 'development') {
          console.log('📊 Intervals ready:', {
            questionId: questionId.substring(0, 8) + '...',
            sessions: timeTrajectory.length
          });
        }
      } else {
        console.log('⚠️ No trajectory sessions found for question:', questionId.substring(0, 8) + '...');
      }
      
      // Don't update cache here - trajectory management should be done in WhiteboardProvider
      
      // Calculate total duration including active sessions
      const totalDuration = timeTrajectory.reduce((total, session) => {
        if (session.duration) {
          // Session with stored duration
          return total + session.duration;
        } else if (session.endDate && session.startDate) {
          // Completed session without stored duration - calculate it
          const duration = (session.endDate - session.startDate) / 1000;
          return total + duration;
        } else if (session.startDate && !session.endDate) {
          // Active session - calculate current duration
          const currentDuration = (Date.now() - session.startDate) / 1000;
          return total + currentDuration;
        }
        return total;
      }, 0);
      
      
      const dataToSave = {
        studentAnswer: answer || '0',  // If null, save as '0'
        trajectory: trajectoryForAnswerHistory || undefined,
        eliminationTrajectory: eliminationTrajectory || undefined,
        intervals: intervals || undefined,
        totalDuration: totalDuration > 0 ? Math.round(totalDuration * 100) / 100 : undefined, // Round to 2 decimal places
        isSkipped: false,
        isEvaluate: true
      };
      
      
      console.log('💾 Saving answer with IDs:', {
        testId,
        studyTaskId,
        questionId: questionId.substring(0, 8) + '...',
        userId: userId.toString(),
        tenantId: tenantId.toString(),
        answer: dataToSave.studentAnswer
      });
      
      await AnswerDataService.saveAnswer(
        testId,
        studyTaskId,
        questionId,
        userId.toString(),
        tenantId.toString(),
        dataToSave
      );
    } catch (error) {
      console.error('❌ Error saving answer:', error);
    }
  }, [testId, studyTaskId, userId, tenantId, isExternalMode]);
  
  // Auto-save disabled - will save on question change or panel close
  // Previously saved on every answer change, now only saves on explicit calls
  
  // Save all pending optical answers - called on question change or panel close
  const savePendingOpticalAnswers = useCallback(async () => {
    if (!userId || !tenantId || !studyTaskId) {
      console.log('❌ Cannot save answers - missing required data:', { userId, tenantId, studyTaskId });
      return;
    }
    
    // In external mode, check if we have Firebase configured
    if (isExternalMode) {
      const hasFirebase = useWhiteboardStore.getState().storageService !== null;
      if (!hasFirebase) {
        console.log('⚠️ External mode without Firebase - skipping answer save');
        return;
      }
      console.log('✅ External mode with Firebase - proceeding with answer save');
    }
    
    // Firebase saves now work in both normal and external modes
    // Authentication has been properly implemented for all Firebase operations
    
    const currentAnswers = opticalAnswersRef.current;
    const savePromises: Promise<void>[] = [];
    
    // Get current test questions to filter valid questionIds
    const currentQuestions = useTestStore.getState().questions;
    const validQuestionIds = new Set(currentQuestions.map(q => q.questionId));
    
    // Get question data cache to check which questions were visited
    const questionDataCache = useWhiteboardStore.getState().questionDataCache;
    
    console.log(`📝 Filtering answers for current test. Total answers: ${Object.keys(currentAnswers).length}, Valid questions: ${validQuestionIds.size}`);
    
    // Check all visited questions (those in the cache)
    questionDataCache.forEach((questionData, questionId) => {
      // Only process if it's a valid question for current test
      if (!validQuestionIds.has(questionId)) {
        return;
      }
      
      // Get the answer (from currentAnswers or questionData)
      const answer = currentAnswers[questionId] || questionData.opticalChoice || '0';
      
      // Check if we need to save (different from last saved)
      if (lastSavedAnswersRef.current[questionId] !== answer) {
        console.log(`💾 Saving answer for question ${questionId.substring(0, 8)}...: ${answer}`);
        // Pass the answer as is - '0' is now a valid OpticalAnswer type
        savePromises.push(saveAnswer(questionId, answer as OpticalAnswer));
        lastSavedAnswersRef.current[questionId] = answer;
      }
    });
    
    if (savePromises.length > 0) {
      try {
        await Promise.all(savePromises);
      } catch (error) {
        console.error('❌ Error saving optical answers:', error);
      }
    } else {
    }
  }, [saveAnswer, isExternalMode, userId, tenantId, studyTaskId]);
  
  // Submit bulk answers to MSSQL
  const submitBulkAnswers = useCallback(async () => {
    if (!bulkSubmitUrl || isExternalMode || !tenantId) {
      return;
    }
    
    setIsSyncing(true);
    setSyncError(null);
    
    try {
      await BulkSyncService.submitTestAnswers(
        testId,
        tenantId.toString(),
        bulkSubmitUrl,
        useAuthStore.getState().accessToken || undefined
      );
      
      setSyncStatus({
        syncedToMssql: true,
        syncAttempts: syncStatus.syncAttempts + 1
      });
      
      onSyncStatusChange?.({
        isSyncing: false,
        syncedToMssql: true,
        syncAttempts: syncStatus.syncAttempts + 1
      });
      
      onBulkSubmitSuccess?.({ testId, studyTaskId });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setSyncError(errorMessage);
      setSyncStatus({
        syncedToMssql: false,
        syncAttempts: syncStatus.syncAttempts + 1
      });
      
      onSyncStatusChange?.({
        isSyncing: false,
        syncedToMssql: false,
        syncAttempts: syncStatus.syncAttempts + 1,
        lastSyncError: errorMessage
      });
      
      onBulkSubmitError?.(error as Error);
    } finally {
      setIsSyncing(false);
    }
  }, [testId, studyTaskId, tenantId, bulkSubmitUrl, isExternalMode, syncStatus, onSyncStatusChange, onBulkSubmitSuccess, onBulkSubmitError]);
  
  // Get sync status
  const getSyncStatus = useCallback(async () => {
    if (isExternalMode || !tenantId) return null;
    
    try {
      const status = await BulkSyncService.getSyncStatus(testId, tenantId.toString());
      setSyncStatus({
        syncedToMssql: status.syncedToMssql,
        syncAttempts: status.syncAttempts
      });
      return status;
    } catch (error) {
      console.error('Error getting sync status:', error);
      return null;
    }
  }, [testId, tenantId, isExternalMode]);
  
  // Mark question as skipped
  const markQuestionSkipped = useCallback(async (questionId: string) => {
    if (!userId || !tenantId || !studyTaskId) {
      console.log('❌ Cannot mark question skipped - missing required data');
      return;
    }
    
    // In external mode, check if we have Firebase configured
    if (isExternalMode) {
      const hasFirebase = useWhiteboardStore.getState().storageService !== null;
      if (!hasFirebase) {
        console.log('⚠️ External mode without Firebase - skipping mark question skipped');
        return;
      }
    }
    
    try {
      // Get the most current cache from the store
      const mostCurrentCache = useWhiteboardStore.getState().questionDataCache;
      const questionData = mostCurrentCache.get(questionId);
      
      // Format intervals from trajectory data
      let intervals: string | null = null;
      if (questionData?.trajectory && questionData.trajectory.length > 0) {
        const formattedIntervals = questionData.trajectory.map(t => ({
          start: new Date(t.startDate).toISOString().replace('T', ' ').substring(0, 19),
          end: t.endDate ? new Date(t.endDate).toISOString().replace('T', ' ').substring(0, 19) : null
        }));
        intervals = JSON.stringify(formattedIntervals);
      }
      
      await AnswerDataService.saveAnswer(
        testId,
        studyTaskId,
        questionId,
        userId.toString(),
        tenantId.toString(),
        {
          isSkipped: true,
          isEvaluate: false,
          intervals: intervals || undefined
        }
      );
    } catch (error) {
      console.error('Error marking question as skipped:', error);
    }
  }, [testId, studyTaskId, userId, tenantId, isExternalMode]);
  
  return {
    isInitialized,
    isSyncing,
    syncError,
    syncStatus,
    saveAnswer,
    savePendingOpticalAnswers,
    submitBulkAnswers,
    getSyncStatus,
    markQuestionSkipped
  };
};